import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { Globe, GraduationCap } from "lucide-react";
import {
  APP_LOGO,
  CURRENT_ORG,
  LOGGED_IN_USER_DATA,
  CURRENT_ORG_IDENTIFIER,
} from "src/config/constants";

const OrganizationSelector = ({ organizations = [] }) => {
  const { t } = useTranslation();
  const [currentOrg, setCurrentOrg] = useState({});
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    // Try to get current org from localStorage
    try {
      const currentOrgString = localStorage.getItem(CURRENT_ORG);
      const org = currentOrgString ? JSON.parse(currentOrgString) : null;
      if (org) {
        console.log("Current organization:", org);
        setCurrentOrg(org);
      } else if (organizations && organizations.length > 0) {
        // If no current org in localStorage but organizations are available, use the first one
        setCurrentOrg(organizations[0]);
        localStorage.setItem(CURRENT_ORG, JSON.stringify(organizations[0]));
        localStorage.setItem(
          CURRENT_ORG_IDENTIFIER,
          organizations[0].identifier
        );
      }
    } catch (error) {
      console.error("Error loading organization data:", error);
    }
  }, [organizations]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleChange = (org) => {
    setCurrentOrg(org);
    localStorage.setItem(CURRENT_ORG, JSON.stringify(org));
    localStorage.setItem(CURRENT_ORG_IDENTIFIER, org.identifier);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t("common:language")}
      >
        <GraduationCap className="w-4 h-4" />
        <span className="text-sm font-medium">
          {organizations && organizations.length > 0
            ? organizations.find(
                (org) => org.identifier === currentOrg.identifier
              )?.name ||
              currentOrg.name ||
              "Organization"
            : currentOrg.name || "Organization"}
        </span>
      </button>
      {open && organizations && organizations.length > 0 && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          {organizations.map((org) => (
            <button
              key={org.identifier}
              onClick={() => {
                handleChange(org);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                currentOrg?.identifier === org.identifier
                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                  : "text-gray-700 dark:text-gray-300"
              }`}
            >
              <span className="text-sm font-medium">{org.name}</span>
              {currentOrg?.identifier === org.identifier && (
                <div className="ml-auto w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationSelector;
